import { UK_MOBILE_VALIDATION_MSG, isUkMobile } from '@/shared/lib/phone-utils';
import { normalisePhone } from '@/shared/lib/validation';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { AlertCircle, Smartphone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAddMobileAccess } from '../../hooks/useUsers';

interface AddMobileAccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName?: string;
  /** Pre-fills the mobile number field (user's work/personal phone). */
  defaultMobileNumber?: string | null;
}

/**
 * Grants the default mobile application access to an existing user.
 *
 * The client/app are resolved server-side from configuration — the only field
 * captured here is the mobile number, pre-filled from the user's stored phone.
 * On success the backend links the user's existing Cognito account to the
 * mobile client/app/role in the secondary entitlements DB.
 */
export function AddMobileAccessDialog({
  open,
  onOpenChange,
  userId,
  userName,
  defaultMobileNumber,
}: AddMobileAccessDialogProps) {
  const addMobileAccess = useAddMobileAccess();
  const [mobileNumber, setMobileNumber] = useState('');
  const [touched, setTouched] = useState(false);

  // Reset the field from the user's stored phone each time the dialog opens.
  // Only pre-fill when the stored value is already a valid UK mobile — a stored
  // landline (workPhone fallback) would silently disable the Grant Access button
  // with no visible error, so we clear it and let the user enter a valid mobile.
  useEffect(() => {
    if (open) {
      const prefill = defaultMobileNumber ?? '';
      setMobileNumber(isUkMobile(prefill) ? prefill : '');
      setTouched(false);
    }
  }, [open, defaultMobileNumber]);

  const isValid = isUkMobile(mobileNumber);
  const showError = touched && mobileNumber.length > 0 && !isValid;

  const handleSubmit = async () => {
    if (!isValid) {
      setTouched(true);
      return;
    }
    await addMobileAccess.mutateAsync({
      userId,
      data: { mobileNumber: normalisePhone(mobileNumber) },
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Mobile Access</DialogTitle>
          <DialogDescription>
            {userName
              ? `Give ${userName} access to the mobile application?`
              : 'Give this user access to the mobile application?'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-4">
          <div className="flex items-start gap-2 text-sm text-muted-foreground">
            <Smartphone className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>
              The user’s existing account will be linked to the mobile application. They can then
              sign in on mobile using their work email.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="mobile-access-number">Mobile Number</Label>
            <Input
              id="mobile-access-number"
              type="tel"
              value={mobileNumber}
              onChange={(e) => setMobileNumber(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder="07911123456"
              className={showError ? 'border-red-500 focus-visible:ring-red-500' : ''}
            />
            {showError && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {UK_MOBILE_VALIDATION_MSG}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={addMobileAccess.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={addMobileAccess.isPending || !isValid}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {addMobileAccess.isPending ? 'Granting…' : 'Grant Access'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
